import React, { useState } from 'react';
import { Button, TextInput, Label, TextField, Switch } from '@scalingworks/react-admin-ui';
import { useTranslate, useNotification } from '@refinedev/core';
import { ModalWrapper } from '~/components';
import { formatCurrency } from '~/resources/helpers';
import { useAdjustCustomerWalletBalanceMutation, usePreviewWalletTopUpBonusQuery } from '~/generated/hooks';

interface Props {
  visible: boolean;
  onOpenChange: (visible: boolean) => void;
  customerId: string;
  currentBalance: number;
  currency: string;
  onSuccess: () => void;
}

export const WalletBalanceAdjustmentModal: React.FC<Props> = ({
  visible,
  onOpenChange,
  customerId,
  currentBalance,
  currency,
  onSuccess,
}) => {
  const t = useTranslate();
  const notification = useNotification();
  
  const [adjustmentType, setAdjustmentType] = useState<'credit' | 'debit'>('credit');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [pin, setPin] = useState('');
  const [applyTopupBonus, setApplyTopupBonus] = useState(false);

  const { mutate: adjustBalance, isLoading } = useAdjustCustomerWalletBalanceMutation({
    onSuccess: () => {
      notification?.open?.({
        message: t('customer.wallet.adjustmentSuccess', 'Wallet balance adjusted successfully'),
        type: 'success',
      });
      onSuccess();
      resetForm();
      onOpenChange(false);
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.errors?.[0]?.message || error.message;
      const errorCode = error?.response?.errors?.[0]?.extensions?.code;
      
      let message = t('customer.wallet.adjustmentError', 'Failed to adjust wallet balance');
      
      if (errorCode === 'INVALID_PIN') {
        message = t('customer.wallet.invalidPin', 'Invalid PIN. Please try again.');
      } else if (errorCode === 'INSUFFICIENT_BALANCE') {
        message = t('customer.wallet.insufficientBalance', 'Insufficient balance for this operation');
      } else if (errorMessage && errorMessage.toLowerCase().includes('pin has expired')) {
        message = t('customer.wallet.pinExpired', 'PIN has expired. Please ask the customer to update their PIN.');
      } else if (errorMessage && errorMessage.toLowerCase().includes('expired')) {
        message = t('customer.wallet.pinExpired', 'PIN has expired. Please ask the customer to update their PIN.');
      }
      
      notification?.open?.({
        message,
        type: 'error',
      });
    },
  });

  const resetForm = () => {
    setAdjustmentType('credit');
    setAmount('');
    setDescription('');
    setPin('');
    setApplyTopupBonus(false);
  };

  const handleCancel = () => {
    resetForm();
    onOpenChange(false);
  };

  const handleConfirm = () => {
    if (!amount || parseFloat(amount) <= 0) {
      notification?.open?.({
        message: t('customer.wallet.invalidAmount', 'Please enter a valid amount'),
        type: 'error',
      });
      return;
    }

    if (!pin || pin.trim().length === 0) {
      notification?.open?.({
        message: t('customer.wallet.pinRequired', 'Please enter the customer PIN'),
        type: 'error',
      });
      return;
    }

    const adjustmentAmount = Math.round(parseFloat(amount) * 100); // Convert to cents and round to integer
    const finalAmount = adjustmentType === 'debit' ? -adjustmentAmount : adjustmentAmount;
    
    adjustBalance({
      customerIdOrPosId: customerId,
      amount: finalAmount,
      description: description || undefined,
      pin,
      applyTopupBonus: adjustmentType === 'credit' ? applyTopupBonus : false,
    });
  };

  const amountInCents = Math.round(parseFloat(amount || '0') * 100);
  
  // Fetch bonus preview for credit adjustments when bonus is enabled
  const { data: bonusPreview, isLoading: bonusLoading } = usePreviewWalletTopUpBonusQuery(
    { amount: amountInCents },
    { 
      enabled: adjustmentType === 'credit' && applyTopupBonus && amountInCents > 0,
      keepPreviousData: true
    }
  );
  
  const bonusAmount = (adjustmentType === 'credit' && applyTopupBonus && bonusPreview?.previewWalletTopUpBonus?.eligible) 
    ? bonusPreview.previewWalletTopUpBonus.bonusAmount 
    : 0;
  
  const totalCreditAmount = (adjustmentType === 'credit' && applyTopupBonus && bonusPreview?.previewWalletTopUpBonus?.eligible)
    ? bonusPreview.previewWalletTopUpBonus.totalAmount
    : amountInCents;
  
  // Calculate new balance based on adjustment type
  const newBalance = adjustmentType === 'credit' 
    ? currentBalance + totalCreditAmount 
    : currentBalance - amountInCents;

  return (
    <ModalWrapper
      open={visible}
      setOpen={onOpenChange}
      title={t('customer.wallet.adjustBalance', 'Adjust Wallet Balance')}
      loading={isLoading}
      onConfirm={handleConfirm}
      confirmButtonTitle={t('common.confirm', 'Confirm')}
      cancelButtonTitle={t('common.cancel', 'Cancel')}
    >
      <section className="p-4">
        <div className="space-y-4">
          {/* Current Balance Display */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">
              {t('customer.wallet.currentBalance', 'Current Balance')}
            </div>
            <div className="text-2xl font-bold text-primary-500">
              {formatCurrency(currentBalance, currency)}
            </div>
          </div>

          {/* Adjustment Type */}
          <div>
            <Label>{t('customer.wallet.adjustmentType', 'Adjustment Type')}</Label>
            <div className="flex gap-2 mt-2">
              <Button
                variant={adjustmentType === 'credit' ? 'solid' : 'outline'}
                size="sm"
                onClick={() => {
                  setAdjustmentType('credit');
                  // Keep applyTopupBonus state when switching to credit
                }}
                className={`flex-1 ${adjustmentType === 'credit' 
                  ? 'bg-green-500 text-white border-green-500' 
                  : 'border-gray-300 text-gray-700'
                }`}
              >
                {t('customer.wallet.credit', 'Add (+)')}
              </Button>
              <Button
                variant={adjustmentType === 'debit' ? 'solid' : 'outline'}
                size="sm"
                onClick={() => {
                  setAdjustmentType('debit');
                  setApplyTopupBonus(false); // Reset topup bonus when switching to debit
                }}
                className={`flex-1 ${adjustmentType === 'debit' 
                  ? '' 
                  : 'border-gray-300 text-gray-700'
                }`}
              >
                {t('customer.wallet.debit', 'Deduct (-)')}
              </Button>
            </div>
          </div>

          {/* Amount */}
          <div>
            <Label required>{t('customer.wallet.amount', 'Amount')}</Label>
            <TextInput
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>

          {/* Apply Topup Bonus - Only show for credit adjustments */}
          {adjustmentType === 'credit' && (
            <div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>{t('customer.wallet.applyTopupBonus', 'Apply Topup Bonus')}</Label>
                  <p className="text-sm text-gray-500 mt-1">
                    {t('customer.wallet.applyTopupBonusHelp', 'Include topup bonus calculation for this credit adjustment')}
                  </p>
                </div>
                <Switch
                  checked={applyTopupBonus}
                  onCheckedChange={setApplyTopupBonus}
                  disabled={isLoading}
                />
              </div>
            </div>
          )}

          {/* Description */}
          <div>
            <Label>{t('customer.wallet.description', 'Description')}</Label>
            <TextField
              type="textarea"
              placeholder={t('customer.wallet.descriptionPlaceholder', 'Enter reason for adjustment')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="h-24"
            />
          </div>

          {/* Customer PIN */}
          <div>
            <Label required>{t('customer.wallet.customerPin', 'Customer PIN')}</Label>
            <TextInput
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="Enter PIN"
              disabled={isLoading}
              className="mt-2"
            />
            <p className="text-sm text-gray-500 mt-2">
              {t('customer.wallet.pinHelp', 'Ask customer for their wallet PIN')}
            </p>
          </div>

          {/* New Balance Preview */}
          {amount && parseFloat(amount) > 0 && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-sm text-blue-600 mb-1">
                {t('customer.wallet.newBalance', 'New Balance After Adjustment')}
              </div>
              
              {/* Show bonus breakdown if applicable */}
              {adjustmentType === 'credit' && applyTopupBonus && bonusAmount > 0 && (
                <div className="space-y-1 mb-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{t('customer.wallet.topupAmount', 'Topup Amount')}:</span>
                    <span className="text-gray-700">{formatCurrency(amountInCents, currency)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-green-600">
                      {t('customer.wallet.bonusAmount', 'Bonus Amount')}
                      {bonusPreview?.previewWalletTopUpBonus?.appliedTier && (
                        <span className="text-gray-500"> ({bonusPreview.previewWalletTopUpBonus.appliedTier.bonusPercentage}%)</span>
                      )}:
                    </span>
                    <span className="text-green-700 font-semibold">+{formatCurrency(bonusAmount, currency)}</span>
                  </div>
                  <div className="border-t pt-1 mt-1"></div>
                </div>
              )}
              
              {/* Show loading state for bonus calculation */}
              {adjustmentType === 'credit' && applyTopupBonus && bonusLoading && (
                <div className="text-xs text-blue-600 mb-2">
                  {t('customer.wallet.calculatingBonus', 'Calculating bonus...')}
                </div>
              )}
              
              {/* Show message when bonus is not eligible */}
              {adjustmentType === 'credit' && applyTopupBonus && bonusPreview && !bonusPreview.previewWalletTopUpBonus?.eligible && (
                <div className="text-xs text-yellow-600 mb-2">
                  {t('customer.wallet.bonusNotEligible', 'This amount is not eligible for bonus')}
                </div>
              )}
              
              <div className="text-2xl font-bold text-blue-700">
                {formatCurrency(newBalance, currency)}
              </div>
            </div>
          )}
        </div>
      </section>
    </ModalWrapper>
  );
};